if (!customElements.get("toggle-collections-tabs")) {
  class FeaturedCollectionsToggleTabs extends HTMLElement {
    constructor() {
      super();

      this.titleEl = this.querySelectorAll("[data-js-tc-title]");
      this.contentEl = this.querySelectorAll("[data-js-tc-content]");
      this.logoEl = this.querySelectorAll(".toggle__collab-logo[data-collection]");
      this.tabRequests = new Map();

      this.titleEl.forEach((title) => {
        title.addEventListener("click", this.onClickHandler.bind(this, title));
        // Start fetching a hidden tab as soon as the shopper shows intent
        const prefetch = () => this.loadTab(this.contentFor(title)).catch(() => {});
        title.addEventListener("pointerenter", prefetch);
        title.addEventListener("focusin", prefetch);
        title.addEventListener("touchstart", prefetch, { passive: true });
      });

      // No keydown handler: the titles are <button> elements, so Enter fires a
      // native click (the old handler called onClickHandler() with no argument
      // and threw on every keypress).

      // A full page opened with ?page=101+ ships the visible tab as a shell too
      const visible = [...this.contentEl].find((content) => content.classList.contains("visible"));
      if (visible?.hasAttribute("data-js-tc-lazy")) {
        this.loadTab(visible).then(() => visible.querySelector("css-slider")?.resetSlider()).catch(() => {});
      }
    };

    contentFor(title) {
      const handle = title.getAttribute("data-collection");
      return [...this.contentEl].find((content) => content.getAttribute("data-collection") === handle);
    };

    // Hidden tabs ship as empty shells (sections/featured-collections-toggle.liquid).
    // The Section Rendering API returns just the asked-for tab when ?page is 100 + its index.
    loadTab(content) {
      if (!content || !content.hasAttribute("data-js-tc-lazy")) return Promise.resolve();

      if (!this.tabRequests.has(content)) {
        const index = content.getAttribute("data-js-tc-index");
        // Full URL, not just the path: ?view= picks the template this section lives in
        const url = new URL(window.location.href);
        url.searchParams.set("section_id", this.dataset.sectionId);
        url.searchParams.set("page", 100 + Number(index));

        const request = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`Tab ${index} request failed: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const panel = window.OrdoUtils.parseHTML(html)
              .querySelector(`[data-js-tc-content][data-js-tc-index="${index}"]:not([data-js-tc-lazy])`);
            if (!panel) throw new Error(`Tab ${index} missing from response`);

            content.replaceChildren(...panel.childNodes);
            // DOMParser never runs the inline script that moves each card's hidden
            // variant input into its form (snippets/quick-buy.liquid), so do it here.
            content.querySelectorAll("form > template").forEach((template) => {
              template.parentElement.append(template.content.cloneNode(true));
            });
            content.removeAttribute("data-js-tc-lazy");
            // Bazaarvoice scans the page once (see assets/component-quick-buy.js), so ask it to star the new cards
            const bv = window.BV || window.$BV;
            try { bv?.ui?.("rr", "inline_ratings"); } catch (e) { /* BV absent or different API */ }
          })
          .catch((error) => {
            this.tabRequests.delete(content);
            throw error;
          });

        this.tabRequests.set(content, request);
      }

      return this.tabRequests.get(content);
    };

    onClickHandler(selectedTitle) {

      if (!selectedTitle) return;

      let currentVisibleTitle = selectedTitle.getAttribute("data-collection");
      let currentVisibleContent;
      // Read before any panel is hidden: a loading tab holds this height so the page doesn't jump
      const previousHeight = [...this.contentEl].find((content) => content.classList.contains("visible"))?.offsetHeight || 0;

      this.titleEl.forEach((title) => {
        if (title.getAttribute("data-collection") === currentVisibleTitle) {
          title.classList.add("visible");
          const container = title.closest(".collection-toggles-container");
          if (container) {
            title.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
          }
        } else {
          title.classList.remove("visible");
        };
      });

      this.logoEl.forEach((logo) => {
        if (currentVisibleTitle === logo.getAttribute("data-collection")) {
          logo.style.display = "";
        } else {
          logo.style.display = "none";
        };
      });

      this.contentEl.forEach((content) => {
        if (currentVisibleTitle === content.getAttribute("data-collection")) {
          if (content.hasAttribute("data-js-tc-lazy")) {
            content.style.minHeight = `${previousHeight}px`;
            content.setAttribute("aria-busy", "true");
            // One retry (a failed request is dropped from the cache) before leaving the page
            this.loadTab(content)
              .catch(() => this.loadTab(content))
              .then(
                () => {
                  if (content.classList.contains("visible")) content.querySelector("css-slider")?.resetSlider();
                },
                (error) => {
                  console.warn("[featured-collections-toggle]", error);
                  // The tab didn't load; still on it: show the full collection instead of an empty panel
                  if (content.classList.contains("visible") && content.dataset.url) window.location.href = content.dataset.url;
                },
              )
              // The tab loaded but didn't lay out: stay on the page
              .catch((error) => console.warn("[featured-collections-toggle]", error))
              .finally(() => {
                content.style.removeProperty("min-height");
                content.removeAttribute("aria-busy");
              });
          }
          currentVisibleContent = content.getAttribute("data-collection");
          content.classList.add("visible");
          content.style.removeProperty("display");
          let display = window.getComputedStyle(content).display;
          if (display === "none") {
            display = "block";
          };
          content.style.display = display;
          content.querySelector('css-slider')?.resetSlider();
        } else {
          content.classList.remove("visible");
          content.style.removeProperty("display");
          let display = window.getComputedStyle(content).display;
          if (display === "block") {
            display = "none";
          };
          content.style.display = display;
        };
      });
    };
  };

  if (typeof customElements.get("toggle-collections-tabs") == "undefined") {
    customElements.define(
      "toggle-collections-tabs",
      FeaturedCollectionsToggleTabs,
    );
  };
};